import { courses } from '@/mocks/courses';
import { categories } from '@/mocks/categories';
import { topics } from '@/mocks/topics';
import type { Course, Category, Topic } from '@/types/course';

export function getAllCourses(): Course[] {
  return courses;
}

export function getCourseById(id: string): Course | undefined {
  return courses.find((c) => c.id === id);
}

export function getCoursesByCategory(categoryId: string): Course[] {
  return courses.filter((c) => c.category === categoryId);
}

export function getLatestCourses(limit = 6): Course[] {
  return [...courses]
    .sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1))
    .slice(0, limit);
}

export function getHotCourses(limit = 4): Course[] {
  return [...courses].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getRelatedCourses(course: Course, limit = 3): Course[] {
  const sameCategory = courses.filter(
    (c) => c.id !== course.id && c.category === course.category,
  );
  const sameSub = sameCategory.filter((c) => c.subcategory === course.subcategory);
  const rest = sameCategory.filter((c) => c.subcategory !== course.subcategory);
  return [...sameSub, ...rest].slice(0, limit);
}

export function getAllCategories(): Category[] {
  return categories;
}

export function getCategoryById(id: string): Category | undefined {
  return categories.find((c) => c.id === id);
}

export function getSubcategories(categoryId: string): string[] {
  return getCategoryById(categoryId)?.subcategories ?? [];
}

export function getAllTopics(): Topic[] {
  return topics;
}

export function getTopicById(id: string): Topic | undefined {
  return topics.find((t) => t.id === id);
}

export function getCoursesByIds(ids: string[]): Course[] {
  return ids
    .map((id) => getCourseById(id))
    .filter((c): c is Course => Boolean(c));
}

export function getRelatedTopics(topicId: string, limit = 3): Topic[] {
  return topics.filter((t) => t.id !== topicId).slice(0, limit);
}

export function searchCourses(keyword: string): Course[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return courses.filter((c) =>
    [c.title, c.intro, c.teacher, c.categoryName, c.subcategory, ...c.tags]
      .join(' ')
      .toLowerCase()
      .includes(kw),
  );
}

export function formatCount(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  return `${n}`;
}

export const levelOptions = ['初级', '中级', '高级'];
export const durationOptions = ['10小时以内', '10-20小时', '20小时以上'];
export const priceTypeOptions = ['免费', '付费'];